/**
 * Harmonic Drift v2.2 - Main Application
 * Generative Audio-Visual Sandbox
 */

import { CONFIG } from './config/index.js';
import { instrumentManager } from './managers/InstrumentManager.js';
import { audioEngine } from './audio/AudioEngine.js';
import { physicsEngine } from './physics/PhysicsEngine.js';
import { canvasRenderer } from './rendering/CanvasRenderer.js';
import { uiManager } from './ui/UIManager.js';
import { inputManager } from './input/InputManager.js';
import { Node } from './entities/Node.js';

class HarmonicDrift {
    constructor() {
        // State
        this.nodes = [];
        this.activeInstruments = [];
        this.isPlaying = false;
        this.animationId = null;
        this.uniqueIdCounter = 100;
        this.beatCounter = 0;
        this.globalEntropy = 0;
        this.lastInteractionTime = 0;
        this.appSettings = {};
        
        // Bind methods
        this.animate = this.animate.bind(this);
        this.onAudioStep = this.onAudioStep.bind(this);
    }

    /**
     * Initialize the application
     */
    init() {
        // Initialize canvas
        const canvas = document.getElementById('canvas');
        canvasRenderer.init(canvas);
        inputManager.init(canvas);
        
        // Initialize UI
        uiManager.init();
        
        // Set up callbacks
        this.setupCallbacks();
        
        console.log('Harmonic Drift initialized');
    }

    /**
     * Set up all callbacks between managers
     */
    setupCallbacks() {
        // UI callbacks
        uiManager.onStartSession = () => this.startSession();
        uiManager.onPausePhysics = () => this.togglePhysics();
        uiManager.onClearNodes = () => this.clearNodes();
        uiManager.onReturnToMenu = () => this.returnToMenu();
        uiManager.onDragStart = (e, instId) => this.startPaletteDrag(e, instId);
        uiManager.onSaveNodeSettings = (nodeId, settings) => this.saveNodeSettings(nodeId, settings);

        // Input callbacks
        inputManager.onCanvasDown = (x, y) => this.handleCanvasDown(x, y);
        inputManager.onCanvasRightClick = (x, y) => this.handleCanvasRightClick(x, y);
        inputManager.onMove = (x, y) => this.handleMove(x, y);
        inputManager.onUp = (x, y) => this.handleUp(x, y);

        // Audio step callback
        audioEngine.onStep = this.onAudioStep;
    }

    /**
     * Start a new session
     */
    async startSession() {
        uiManager.showLoading();

        // Get settings
        this.appSettings = uiManager.getSettings();
        
        // Configure audio
        CONFIG.baseTempo = this.appSettings.bpm;
        CONFIG.globalSpeedMult = this.appSettings.speed;
        audioEngine.setScale(this.appSettings.scale);
        audioEngine.setTempo(this.appSettings.bpm);

        // Configure physics
        physicsEngine.setPausedState(!this.appSettings.enablePhysics);
        uiManager.setPauseButtonState(!this.appSettings.enablePhysics);


        // Get active instruments
        this.activeInstruments = instrumentManager.getActiveSet(this.appSettings.paletteMode);

        // If in battery mode, ensure battery is at the start of the palette
        if (this.appSettings.batteryMode) {
            // Remove battery if it exists (it might be in DEFAULT_INSTRUMENTS)
            this.activeInstruments = this.activeInstruments.filter(i => i.id !== 'sys_battery');
            // Add battery at the beginning
            const batteryInst = instrumentManager.findById('sys_battery');
            if (batteryInst) {
                this.activeInstruments.unshift(batteryInst);
            }
        }

        try {
            // Initialize audio with instruments
            await audioEngine.init(this.activeInstruments);
            
            // Build sidebar
            uiManager.buildSidebar(this.activeInstruments);
            
            // Update HUD
            uiManager.updateHUD({ scale: this.appSettings.scale });
            
            // Start playback
            this.isPlaying = true;
            physicsEngine.setPaused(false);
            this.lastInteractionTime = Date.now();
            audioEngine.start();
            
            // Start animation loop
            this.animate();
            
            // Transition UI
            uiManager.showPlayingState();
            
            // Hide menu background animation
            const menuBgCanvas = document.getElementById('menu-bg-canvas');
            if (menuBgCanvas) menuBgCanvas.style.display = 'none';
            if (window.menuBgAnimation) window.menuBgAnimation.stop();
            
        } catch (e) {
            console.error('Failed to start session:', e);
            uiManager.showError("Audio init failed: " + e.message);
            uiManager.hideLoading();
        }
    }

    /**
     * Handle audio step (called every 16th note)
     * @param {number} time - Tone.js time
     */
    onAudioStep(time) {
        this.beatCounter++;
        
        // Trigger background pulse every measure
        if (this.beatCounter % 16 === 0) {
            canvasRenderer.triggerPulse();
        }

        // Update entropy
        const timeSinceInteract = (Date.now() - this.lastInteractionTime) / 1000;
        if (timeSinceInteract > CONFIG.physics.entropyInteractionTimeout / 1000) {
            this.globalEntropy = Math.min(1, this.globalEntropy + CONFIG.physics.entropyGrowthRate);
        } else {
            this.globalEntropy = Math.max(0, this.globalEntropy - CONFIG.physics.entropyDecayRate);
        }

        // Update filter based on entropy
        const filterFreq = Math.min(20000, Math.max(200, 5000 * (1 - this.globalEntropy)));
        audioEngine.setFilterFrequency(filterFreq);

        // Update visualizer
        const progress = (this.beatCounter % 16) / 16;
        uiManager.updateVisualizer(progress);

        // Tick all nodes
        this.nodes.forEach(node => {
            // Batteries never make sound
            if (node.instDef.id === 'sys_battery') return;
            
            // In battery mode, only tick nodes that are "powered"
            if (this.appSettings.batteryMode) {
                const isPowered = this.isNodePowered(node);
                if (isPowered) {
                    node.onTick(time, this.beatCounter);
                }
            } else {
                node.onTick(time, this.beatCounter);
            }
        });
    }

    /**
     * Main animation loop
     */
    animate() {
        if (!this.isPlaying) return;
        
        this.animationId = requestAnimationFrame(this.animate);

        const { width, height } = canvasRenderer.getDimensions();
        const mouse = inputManager.getMouse();

        // Update physics
        physicsEngine.resolveCollisions(this.nodes);

        // Update node connections for physics-based modulation
        this.nodes.forEach(node => {
            node.updateConnections(this.nodes);
        });

        // Update nodes
        this.nodes.forEach(node => {
            node.update(mouse, physicsEngine.isPaused(), this.globalEntropy, CONFIG.globalSpeedMult);
        });

        // Get dragged instrument for preview
        const draggedInstId = inputManager.getDraggedInstId();
        const draggedInst = draggedInstId 
            ? this.activeInstruments.find(i => i.id === draggedInstId) 
            : null;

        // Render
        canvasRenderer.render(this.nodes, mouse, draggedInst);

        // Update HUD
        uiManager.updateHUD({
            entropy: this.globalEntropy,
            nodeCount: this.nodes.length
        });
    }

    /**
     * Handle canvas down event
     * @param {number} x - X coordinate
     * @param {number} y - Y coordinate
     */
    handleCanvasDown(x, y) {
        this.lastInteractionTime = Date.now();

        // Check if clicking on an existing node
        for (let i = this.nodes.length - 1; i >= 0; i--) {
            const node = this.nodes[i];
            if (node.containsPoint(x, y)) {
                node.locked = true;
                inputManager.setDraggedNode(node);
                node.trigger();
                return;
            }
        }
    }

    /**
     * Handle canvas right-click event (for node editor in controlled mode)
     * @param {number} x - X coordinate
     * @param {number} y - Y coordinate
     */
    handleCanvasRightClick(x, y) {
        if (!this.appSettings.controlledMode) return;

        // Check if right-clicking on an existing node
        for (let i = this.nodes.length - 1; i >= 0; i--) {
            const node = this.nodes[i];
            if (node.containsPoint(x, y)) {
                uiManager.openNodeEditor(node);
                return;
            }
        }
    }

    /**
     * Handle move event
     * @param {number} x - X coordinate  
     * @param {number} y - Y coordinate
     */
    handleMove(x, y) {
        this.lastInteractionTime = Date.now();
        
        const screenCoords = inputManager.getScreenCoords();
        const draggedInstId = inputManager.getDraggedInstId();
        const draggedNode = inputManager.getDraggedNode();
        
        // Check if over trash
        if ((draggedInstId || draggedNode) && uiManager.isOverTrash(screenCoords.x, screenCoords.y)) {
            uiManager.setTrashActive(true);
        } else {
            uiManager.setTrashActive(false);
        }
    }

    /**
     * Handle up event
     * @param {number} x - X coordinate
     * @param {number} y - Y coordinate
     */
    handleUp(x, y) {
        const { width, height } = canvasRenderer.getDimensions();
        const screenCoords = inputManager.getScreenCoords();
        const draggedInstId = inputManager.getDraggedInstId();
        const draggedNode = inputManager.getDraggedNode();

        if (draggedInstId) {
            // Dropping an instrument from palette
            const isOverSidebar = uiManager.isOverSidebar(screenCoords.x, screenCoords.y);
            
            if (!isOverSidebar && inputManager.isInBounds(x, y, width, height)) {
                const inst = this.activeInstruments.find(i => i.id === draggedInstId);
                if (inst) {
                    this.createNode(inst, x, y);
                }
            }
            inputManager.clearDrag();
        }

        if (draggedNode) {
            // Releasing a dragged node
            if (uiManager.isOverTrash(screenCoords.x, screenCoords.y)) {
                this.removeNode(draggedNode.id);
            }
            draggedNode.locked = false;
            inputManager.setDraggedNode(null);
        }

        uiManager.setTrashActive(false);
    }

    /**
     * Saves the settings for a specific node from the editor modal.
     * @param {string} nodeId The ID of the node to update.
     * @param {object} settings The new settings.
     */
    saveNodeSettings(nodeId, settings) {
        const node = this.nodes.find(n => n.id == nodeId);
        if (node) {
            node.isFixed = settings.isFixed;
            node.isMuted = settings.isMuted;

            // If the node is now fixed, stop its movement
            if (node.isFixed) {
                node.vx = 0;
                node.vy = 0;
            }
        }
    }

    /**
     * Check if a node is powered in battery mode
     * @param {Node} node - The node to check
     * @returns {boolean}
     */
    isNodePowered(node) {
        if (node.instDef.id === 'sys_battery') return true; // Batteries are always powered

        const batteries = this.nodes.filter(n => n.instDef.id === 'sys_battery');
        if (batteries.length === 0) return false; // No batteries = nothing is powered

        for (const battery of batteries) {
            const dx = battery.x - node.x;
            const dy = battery.y - node.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < CONFIG.connectionDistance) {
                return true;
            }
        }
        return false;
    }

    /**
     * Start dragging an instrument from the palette
     * @param {Event} e - Input event
     * @param {string} instId - Instrument ID
     */
    startPaletteDrag(e, instId) {
        inputManager.startDrag(e, instId);
    }

    /**
     * Create a new node
     * @param {Object} inst - Instrument definition
     * @param {number} x - X position
     * @param {number} y - Y position
     */
    createNode(inst, x, y) {
        const { width, height } = canvasRenderer.getDimensions();
        const node = new Node(this.uniqueIdCounter++, inst, x, y, width, height);
        
        // If physics is disabled, nodes should be fixed in place
        if (!this.appSettings.enablePhysics) {
            node.vx = 0;
            node.vy = 0;
            node.isFixed = true;
        }
        
        // Batteries don't make sound
        if (inst.id !== 'sys_battery') {
            node.trigger();
        }
        this.nodes.push(node);
    }

    /**
     * Remove a node by ID
     * @param {number} id - Node ID
     */
    removeNode(id) {
        this.nodes = this.nodes.filter(n => n.id !== id);
    }

    /**
     * Clear all nodes
     */
    clearNodes() {
        this.nodes = [];
    }

    /**
     * Toggle physics pause
     */
    togglePhysics() {
        const paused = physicsEngine.togglePause();
        uiManager.setPauseButtonState(paused);
    }

    /**
     * Return to main menu
     */
    returnToMenu() {
        this.isPlaying = false;
        
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
        
        audioEngine.pause();
        uiManager.showMenuState();
        
        // Restore menu background animation
        const menuBgCanvas = document.getElementById('menu-bg-canvas');
        if (menuBgCanvas) menuBgCanvas.style.display = 'block';
        if (window.menuBgAnimation) window.menuBgAnimation.start();
    }
}

// Initialize on DOM ready
const app = new HarmonicDrift();
document.addEventListener('DOMContentLoaded', () => app.init());

export default app;
